import { useEffect, useState } from 'react';
import { api, resolveMediaUrl } from '../../../apis/api';
import '../orders/orders.css';
import '../categories/categories.css';
import './paymentStatus.css';

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const payColors = {
  pending: 'status--pending',
  confirmed: 'status--shipped',
  paid: 'status--delivered',
  rejected: 'status--cancelled',
};

function getProofUrl(order) {
  if (order.payment_proof_url) return resolveMediaUrl(order.payment_proof_url);
  if (order.payment_proof) {
    const file = String(order.payment_proof).split('/').pop();
    return resolveMediaUrl(`/uploads/payments/${file}`);
  }
  return '';
}

export const PaymentStatus = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getOrders(filter ? { payment_status: filter } : {});
      setOrders(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      setOrders([]);
      setError(err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updatePayment = async (orderId, payment_status) => {
    try {
      await api.updatePaymentStatus(orderId, payment_status);
      await load();
    } catch (err) {
      alert(err.message || 'Failed to update payment status');
    }
  };

  if (loading) return <p className="admin-loading">Loading payment status…</p>;
  if (error) return <p className="admin-error">{error}</p>;

  return (
    <div className="admin-payments">
      <div className="admin-orders__filters">
        <select className="admin-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All Payments</option>
          {['pending', 'confirmed', 'paid', 'rejected'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>INVOICE</th>
              <th>CUSTOMER</th>
              <th>METHOD</th>
              <th>PAYMENT IMAGE</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>DATE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={8}>No payment records</td></tr>
            ) : (
              orders.map((o) => {
                const proof = getProofUrl(o);
                return (
                  <tr key={o.order_id}>
                    <td>{o.invoice_no}</td>
                    <td>
                      <div>{o.customer_name}</div>
                      <small>{o.customer_email}</small>
                    </td>
                    <td>{(o.payment_method || '—').toUpperCase()}</td>
                    <td>
                      {proof ? (
                        <button
                          type="button"
                          className="admin-payments__proof-btn"
                          onClick={() => setPreview(proof)}
                          aria-label={`View payment proof for ${o.invoice_no}`}
                        >
                          <img
                            src={proof}
                            alt={`Payment proof ${o.invoice_no}`}
                            className="admin-payments__proof-img"
                          />
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{formatINR(o.total)}</td>
                    <td>
                      <span className={`status-badge ${payColors[o.payment_status] || 'status--pending'}`}>
                        {o.payment_status || 'pending'}
                      </span>
                    </td>
                    <td>{formatDate(o.created_at)}</td>
                    <td>
                      <select
                        className="admin-select"
                        value={o.payment_status || 'pending'}
                        onChange={(e) => updatePayment(o.order_id, e.target.value)}
                      >
                        {['pending', 'confirmed', 'paid', 'rejected'].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="admin-modal-backdrop" onClick={() => setPreview('')}>
          <div
            className="admin-modal admin-payments__preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={preview} alt="Payment proof" className="admin-payments__preview-img" />
          </div>
        </div>
      )}
    </div>
  );
};
